import { ArrowRight } from "lucide-react";
import CTABanner from "@/components/CTABanner";
import Reveal from "@/components/Reveal";
import { useContactModal } from "@/context/ContactModalContext";
import { IMAGES } from "@/lib/content";

const PRINCIPLES = [
  {
    n: "01",
    t: "Independence",
    d: "We work without conflicts of interest, providing advice that is genuinely in our clients' best interest.",
  },
  {
    n: "02",
    t: "Senior-led",
    d: "Every engagement is led directly by partners with deep advisory and operating experience.",
  },
  {
    n: "03",
    t: "Pragmatic",
    d: "We focus on decisions that change outcomes — and the work required to make them well.",
  },
  {
    n: "04",
    t: "Discreet",
    d: "We work quietly alongside leadership teams, without the noise of larger firms.",
  },
];

const About = () => {
  const { open } = useContactModal();

  return (
    <div data-testid="about-page">
      {/* HERO — image left, headline right */}
      <section className="border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-0 lg:pt-0 pb-20 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <div className="relative aspect-[5/6] overflow-hidden">
                <img
                  src={IMAGES.reception}
                  alt="Sable Advisory reception"
                  className="w-full h-full object-cover object-[center_45%] brightness-[1.08] contrast-[1.03]"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/18" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-transparent to-[#0A0A0A]/45" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/35 via-transparent to-[#0A0A0A]/35" />
                <div className="absolute inset-0 shadow-[inset_0_0_90px_rgba(10,10,10,0.72)]" />
              </div>
            </div>

            <div className="lg:col-span-6">
              <span className="eyebrow">About us —</span>
              <h1 className="mt-8 font-serif text-[40px] sm:text-[52px] lg:text-[60px] leading-[1.06] text-offwhite tracking-[-0.015em]">
                Independent advice.
                <br />
                Commercial insight.
                <br />
                Lasting impact.
              </h1>
              <div className="mt-10 space-y-5 max-w-[52ch] text-[15px] leading-[1.8] text-offwhite/70">
                <p>
                  We are a team of experienced advisors with deep expertise
                  across strategy, regulation, digital, and transactions.
                </p>
                <p>
                  Our approach is collaborative, pragmatic, and tailored to the
                  challenges our clients face — without the layers, conflicts,
                  or theatrics of larger firms.
                </p>
              </div>
              <a
                href="#approach"
                data-testid="about-approach-link"
                className="mt-10 link-underline text-[14px] text-offwhite/85"
              >
                Our approach
                <ArrowRight size={16} className="hover-arrow" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH / PRINCIPLES */}
      <section
        id="approach"
        data-testid="about-approach"
        className="border-b border-hairline bg-[#0B0B0B]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <span className="eyebrow">Our approach</span>
              <h2 className="mt-6 font-serif text-[34px] lg:text-[42px] leading-[1.12] text-offwhite max-w-[16ch]">
                A different way of working with leadership teams.
              </h2>
              <p className="mt-8 text-[14.5px] leading-[1.8] text-offwhite/65 max-w-[42ch]">
                We are deliberately small. Every engagement is led directly by
                partners and shaped around the specific decisions our clients
                face.
              </p>
            </div>

            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-12">
              {PRINCIPLES.map((p, idx) => (
                <Reveal key={p.n} delay={idx * 110}>
                  <div
                    data-testid={`principle-${p.n}`}
                    className="border-t border-hairline pt-6"
                  >
                    <div className="flex items-baseline gap-4 mb-4">
                      <h3 className="font-serif text-[24px] text-[#D4B66F]">
                        {p.t}
                      </h3>
                    </div>
                    <p className="text-[14px] leading-[1.75] text-[#8A8A8A]">
                      {p.d}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE TONE — quote-style */}
      <section data-testid="about-experience" className="border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={IMAGES.officeDark}
                  alt="Sable Advisory office"
                  className="w-full h-full object-cover object-[52%_48%] brightness-[1.06] contrast-[1.03]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/30" />
              </div>
            </div>
            <div className="lg:col-span-7 lg:pt-6">
              <span className="eyebrow">Experience</span>
              <p className="mt-8 font-serif text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.25] text-offwhite italic max-w-[24ch]">
                "Strategic decisions are rarely about answers. They are about
                framing the right questions, clearly."
              </p>
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-10">
                {[
                  {
                    k: "20+ yrs",
                    d: "Combined senior advisory and operating experience across our partners.",
                  },
                  {
                    k: "4 practices",
                    d: "Strategy, regulatory, digital and transactions — connected by design.",
                  },
                  {
                    k: "Sector depth",
                    d: "Technology, media, digital platforms, and investment.",
                  },
                ].map((s, idx) => (
                  <Reveal key={s.k} delay={idx * 120}>
                    <div className="h-px w-10 bg-gold mb-5" />
                    <p className="font-serif text-[22px] text-[#D4B66F] mb-2">
                      {s.k}
                    </p>
                    <p className="text-[13px] leading-[1.7] text-offwhite/55">
                      {s.d}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default About;
